<!-- eslint-disable vue/first-attribute-linebreak -->
<!-- Period picker for the Customer Behavior page: a button that opens a month / quarter / year grid
     (with year paging and a jump-to-year view) instead of a long dropdown, so it stays usable with
     50–100 periods of data. -->
<template>
  <div ref="root" class="cb-picker">
    <button type="button" class="cb-picker-btn" :aria-expanded="open" aria-haspopup="dialog"
      :aria-label="`เลือกช่วง ${slotName}`" @click="toggle">
      <i class="material-icons material-symbols-outlined" aria-hidden="true">calendar_month</i>
      <span class="cb-picker-label">{{ modelValue ? labelOf(modelValue, true) : "ไม่เทียบ" }}</span>
      <i class="material-icons material-symbols-outlined" aria-hidden="true">expand_more</i>
    </button>

    <div v-if="open" class="cb-picker-pop" role="dialog" :aria-label="`เลือกช่วง ${slotName}`">
      <div class="cb-picker-head">
        <template v-if="gran !== 'year' && view === 'grid'">
          <button type="button" class="cb-picker-nav" aria-label="ปีก่อนหน้า" :disabled="yearIndex <= 0"
            @click="year = years[yearIndex - 1]">
            <i class="material-icons material-symbols-outlined" aria-hidden="true">chevron_left</i>
          </button>
          <button type="button" class="cb-picker-title" aria-label="เลือกปี" @click="view = 'years'">
            ปี {{ +year + 543 }}
            <i class="material-icons material-symbols-outlined" aria-hidden="true">arrow_drop_down</i>
          </button>
          <button type="button" class="cb-picker-nav" aria-label="ปีถัดไป" :disabled="yearIndex >= years.length - 1"
            @click="year = years[yearIndex + 1]">
            <i class="material-icons material-symbols-outlined" aria-hidden="true">chevron_right</i>
          </button>
        </template>
        <span v-else class="cb-picker-title static">{{ gran === "year" ? "เลือกปี" : "เลือกปี (พ.ศ.)" }}</span>
      </div>

      <!-- jump to a year (month / quarter views) -->
      <div v-if="gran !== 'year' && view === 'years'" class="cb-picker-grid cols-3">
        <button v-for="y in years" :key="y" type="button" class="cb-pick" :aria-pressed="y === year"
          @click="year = y; view = 'grid'">{{ +y + 543 }}</button>
      </div>

      <div v-else class="cb-picker-grid" :class="{ 'cols-2': gran === 'quarter', 'cols-3': gran === 'year' }">
        <button v-for="it in items" :key="it.k" type="button" class="cb-pick"
          :class="{ warm: has(it.k) && isWarm(it.k) }" :disabled="!has(it.k)"
          :aria-pressed="modelValue === it.k" :title="has(it.k) && isWarm(it.k) ? 'ช่วงตั้งต้น ตั๋วส่วนใหญ่ยังไม่ครบกำหนด' : null"
          @click="choose(it.k)">
          {{ it.main }}<small v-if="it.sub">{{ it.sub }}</small>
          <span v-if="otherValue === it.k" class="cb-mark" :class="otherName === 'A' ? 'a' : 'b'"
            aria-hidden="true">{{ otherName }}</span>
        </button>
      </div>

      <div class="cb-picker-foot">
        <span><span class="cb-dash" aria-hidden="true"></span>เส้นประ = ช่วงตั้งต้น</span>
        <button v-if="allowClear" type="button" class="btn btn-sm btn-outline-primary mb-0" @click="choose(null)">ไม่เทียบ</button>
        <button v-else type="button" class="btn btn-sm btn-outline-primary mb-0" @click="choose(periods[periods.length - 1])">ช่วงล่าสุด</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  modelValue: { type: String, default: null },
  gran: { type: String, required: true },          // month | quarter | year
  periods: { type: Array, required: true },        // period keys with data, ascending
  slotName: { type: String, required: true },      // "A" | "B"
  otherValue: { type: String, default: null },     // the other slot's period, marked in the grid
  allowClear: { type: Boolean, default: false },
  labelOf: { type: Function, required: true },     // (key, long) => display label
  isWarm: { type: Function, required: true },      // (key) => true for warm-up periods
});
const emit = defineEmits(["update:modelValue"]);

const TH_M = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const root = ref(null);
const open = ref(false);
const view = ref("grid");
const year = ref(null);

const otherName = computed(() => (props.slotName === "A" ? "B" : "A"));
const years = computed(() => [...new Set(props.periods.map((k) => k.slice(0, 4)))]);
const yearIndex = computed(() => years.value.indexOf(year.value));
const has = (k) => props.periods.includes(k);

const items = computed(() => {
  if (props.gran === "month") return TH_M.map((m, i) => ({ k: `${year.value}-${String(i + 1).padStart(2, "0")}`, main: m }));
  if (props.gran === "quarter") {
    return [1, 2, 3, 4].map((q) => ({ k: `${year.value}-Q${q}`, main: `ไตรมาส ${q}`, sub: `${TH_M[q * 3 - 3]} – ${TH_M[q * 3 - 1]}` }));
  }
  return years.value.map((y) => ({ k: y, main: String(+y + 543), sub: `ค.ศ. ${y}` }));
});

function toggle() {
  if (open.value) { open.value = false; return; }
  const k = props.modelValue || props.otherValue || props.periods[props.periods.length - 1] || "";
  year.value = k.slice(0, 4);
  view.value = "grid";
  open.value = true;
}

function choose(k) {
  open.value = false;
  emit("update:modelValue", k);
}

function onDocClick(e) {
  if (open.value && root.value && !root.value.contains(e.target)) open.value = false;
}
function onKey(e) {
  if (open.value && e.key === "Escape") open.value = false;
}
onMounted(() => {
  // capture phase: the grid re-renders on navigation, so the clicked node may already be detached
  // by the time a bubbling listener runs and would look like an outside click
  document.addEventListener("click", onDocClick, true);
  document.addEventListener("keydown", onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocClick, true);
  document.removeEventListener("keydown", onKey);
});
</script>

<style lang="scss" scoped>
.cb-picker { position: relative; flex: 1; min-width: 0; }
.cb-picker-btn {
  width: 100%; display: flex; align-items: center; gap: 8px; padding: 7px 10px;
  border: 1px solid #d2d6da; border-radius: 0.375rem; background: #fff; color: #344767;
  font-size: 0.875rem; text-align: left; cursor: pointer;
  i { font-size: 18px; color: #67748e; }
  &:hover, &[aria-expanded="true"] { border-color: #05af50; }
  &[aria-expanded="true"] { box-shadow: inset 0 0 0 1px #05af50; }
}
.cb-picker-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cb-picker-pop {
  position: absolute; top: calc(100% + 6px); left: 0; z-index: 1050; width: 304px; max-width: calc(100vw - 32px);
  padding: 12px; background: #fff; border-radius: 0.75rem;
  box-shadow: 0 8px 26px -4px rgba(20, 20, 20, 0.18), 0 8px 9px -5px rgba(20, 20, 20, 0.08);
}
.cb-picker-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
.cb-picker-nav {
  width: 32px; height: 32px; padding: 0; border: 0; border-radius: 50%; background: transparent; color: #344767;
  display: grid; place-items: center; cursor: pointer;
  &:hover:not(:disabled) { background: #f0f2f5; }
  &:disabled { opacity: 0.3; cursor: default; }
}
.cb-picker-title {
  display: inline-flex; align-items: center; gap: 2px; padding: 4px 10px; border: 0; border-radius: 6px;
  background: transparent; color: #344767; font-weight: 700; cursor: pointer;
  i { font-size: 18px; }
  &:hover { background: #f0f2f5; }
  &.static { cursor: default; }
}
.cb-picker-grid {
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px;
  &.cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  &.cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
.cb-pick {
  position: relative; display: flex; flex-direction: column; align-items: center; padding: 9px 4px;
  border: 1px solid #dee2e6; border-radius: 8px; background: #fff; color: #344767; font-size: 0.875rem;
  line-height: 1.3; cursor: pointer;
  small { font-size: 11px; color: #7b809a; }
  &:hover:not(:disabled) { border-color: #05af50; background: #e8fef2; }
  &:disabled { color: #ced4da; background: #f8f9fa; border-color: transparent; cursor: default; }
  &.warm { border-style: dashed; }
  &[aria-pressed="true"] { background: #05af50; border: 1px solid #05af50; color: #fff; small { color: rgba(255, 255, 255, 0.85); } }
}
.cb-mark {
  position: absolute; top: -7px; right: -7px; width: 18px; height: 18px; border-radius: 5px;
  display: grid; place-items: center; font-size: 10px; font-weight: 700; box-shadow: 0 0 0 2px #fff;
  &.a { background: #05af50; color: #fff; }
  &.b { background: #ced4da; color: #344767; }
}
.cb-picker-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 12px; padding-top: 10px;
  border-top: 1px solid #dee2e6; font-size: 12px; color: #7b809a;
}
.cb-dash { display: inline-block; width: 14px; height: 10px; margin-right: 4px; border: 1px dashed #67748e; border-radius: 3px; vertical-align: -1px; }
</style>
